import { List as ImmutableList } from 'immutable';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors, chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { customersSelectors } from 'app-components/flux/customers';
import { payeesSelectors } from 'app-components/flux/payees';
import { tagsSelectors } from 'app-components/flux/tags';

import {
  DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION,
  QCS_ID_FOR_CLIENT_ONLY_UI_OPTION,
  VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION,
} from 'components/QFilterSelect/constants';

export const dataDictionary = ImmutableList([
  {
    id: 'actionsSelect',
    type: 'actionsSelect',
    label: ' ',
    flex: 1,
    hide: false,
  },
  {
    id: 'name', // the name/index of the field in the data record
    type: 'string', // 'string' or 'select'
    label: 'Category Name', // Data label for this field
    flex: 3, // in list display, flex distribution
    editable: false, // in category row, is the field editable?
    uniqueWith: ['parent'], // this field, and all fields in the array must match to be considered a duplicate
    placeholder: 'Tag',
    hide: false,
    selectable: true,
  },
]);

//
// Configuration for resources supported
//
export const resourceConfig = {
  categories: {
    key: 'categoriesById',
    selector: categoriesSelectors.getCategoriesById,
    allowsChildren: true,
  },
  clientsAndProjects: {
    key: 'customersById',
    selector: customersSelectors.getCustomers,
    allowsChildren: true,
  },
  accounts: {
    key: 'accountsById',
    selector: accountsSelectors.getAccountsById,
    allowsChildren: false,
  },
  payeeNames: {
    key: 'payeesNameList',
    selector: payeesSelectors.getPayeesNamesSorted,
    allowsChildren: false,
  },
  tags: {
    key: 'tagsById',
    selector: tagsSelectors.getTags,
    allowsChildren: false,
  },
};

// ========== CACHE FOR SPEED ===========
let PARENT_CACHE = {};

function resetParentCache() {
  PARENT_CACHE = {};
}
function addToParentCache(key, value) {
  PARENT_CACHE[key] = value;
}
function parentCacheValue(key) {
  return PARENT_CACHE[key];
}
// ========== CACHE FOR SPEED ===========

function crawlList(coaNode, categories, filter, level = 1) {
  const category = coaNode; // categories.get(coaNode.id);

  if (category && !category.isExcludedFromCategoryList && category.inList && category.id !== 'NONE') {
    let childData = null;
    if (coaNode.children) {
      childData = [];
      coaNode.children.forEach((node) => {
        childData = childData.concat(crawlList(node, categories, filter, level + 1));
      });
    }

    childData = childData?.filter((x) => x.id !== 0);

    if (filter && category.fullPathName.toLowerCase().indexOf(filter.toLowerCase()) === -1 && !childData?.length) {
      return [{ id: 0 }];
    }

    const parent = Number(category.parentId)
      ? parentCacheValue(category.parentId) ||
        chartOfAccountsSelectors.getCoaStringSelector(undefined, { type: 'CATEGORY', id: category.parentId }, true)
      : '';

    addToParentCache(category.parentId, parent);

    const parentCatType = ['', '0'].includes(category.parentId)
      ? chartOfAccountsTypes.CoaTypeEnum.NONE
      : chartOfAccountsTypes.CoaTypeEnum.CATEGORY;

    return [
      {
        id: category.id,
        name: category.name,
        description: category.description,
        categoryType: String(category.type),
        isExcludedFromBudgets: category.isExcludedFromBudgets ? 'No' : 'Yes',
        isDeleted: category.isDeleted,
        parent,
        // if an object 'values' is included in the data record, the value specified for the given id
        // is used for the inputComponent instead of the string/number provided in the base object
        // (e.g. for 'parent', instead of the string above, we will use the coa record below)
        values: {
          parent: { type: parentCatType, id: category.parentId },
        },
        // if the array property children is specified, then the objects in the array represent a hierarchical
        // section (child objects) of the parent
        children: childData,
        level,
      },
    ];
  }
  return [{ id: 0 }];
}

export function makeLocalCategoriesData({ categories, chartOfAccounts, filter, filterFn }) {
  if (!categories) {
    return [];
  }

  resetParentCache();

  let data;
  if (chartOfAccounts) {
    let data2 = [];
    chartOfAccounts.forEach((coaNode) => {
      const ret = crawlList(coaNode, categories, filter);
      data2 = data2.concat(ret);
    });
    data = ImmutableList(data2).filter((x) => Number(x.id) !== 0);
  }

  return filterFn ? data.filter(filterFn) : data;
}

/**
 * Method constructs data for clients and projects filter in a format that is needed for tree list display.
 * customer(client) name is parent node and project name is child node.
 * @param customersById - List of all customers(clients).
 * @param projectsByCustomerId - List of all projects for every customer(client).
 * @param searchFilter - Filter text entered by user
 * @returns data needed by tree component
 */
export function makeClientsAndProjectsData({ customersById, projectsByCustomerId, searchFilter }) {
  const result = [];
  customersById.forEach((customer) => {
    // Create child nodes (projects) for this customer node
    const projectList = [];
    const activeProjects = projectsByCustomerId.get(customer.id)?.activeProjects;
    const completedProjects = projectsByCustomerId.get(customer.id)?.completedProjects;
    /*
      Logic for adding child nodes:
      1) If client has at least 1 project, we need to display a speical option called "Client only" 
      2) Display all open projects in alphabetical order of name
      3) Display  all completed(closed) projects in alphabetical order of name( Also append 'Closed' to project name)
    */
    if (activeProjects?.size > 0 || completedProjects?.size > 0) {
      /* Add a special child node called "Client Only"
      *  When we send filter data to QCS for this project, QCS expects to include clientId and projectId(value as 0) in request payload 
        in following format
        {
          "customer": "clientId",
          "projects": [0], 
        }
        Since we need to keep reference of which customerId this "Client Only" UI option belongs to, we will store ID in following format
        id: `${customer.id}:0`  
      */
      // Make sure name used here matches any filter text
      if (!searchFilter || DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION.toLowerCase().includes(searchFilter.toLowerCase())) {
        projectList.push({
          // We store option value in <client_name>:<qcs_id_for_client_only_option_id> format.
          // This ways, when we later read selected value, we can determine which client this option belongs to.
          id: `${customer.id}${VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION}${QCS_ID_FOR_CLIENT_ONLY_UI_OPTION}`,
          name: DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION,
        });
      }
    }
    if (activeProjects?.size > 0) {
      // Add all active projects as child nodes under this customer
      activeProjects
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((project) => {
          // Make sure project name matches any filter text
          if (!searchFilter || project?.name.toLowerCase().includes(searchFilter.toLowerCase())) {
            projectList.push({
              id: project.id,
              name: project.name,
            });
          }
        });
    }
    if (completedProjects?.size > 0) {
      // Add all completed projects as child nodes under this customer
      completedProjects
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((project) => {
          // Make sure project name matches any filter text
          if (!searchFilter || project?.name.toLowerCase().includes(searchFilter.toLowerCase())) {
            projectList.push({
              id: project.id,
              name: `${project.name} (completed)`,
            });
          }
        });
    }
    const customerName =
      customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
    // If there is any filter text entered by user, make sure client name or project name matches filter text
    if (!searchFilter || customerName.toLowerCase().includes(searchFilter.toLowerCase()) || projectList.length > 0) {
      result.push({
        id: customer.id,
        name: customerName,
        children: projectList,
      });
    }
  });
  // Sort customers based on names
  result.sort((a, b) => a.name.localeCompare(b.name));
  return ImmutableList(result);
}

export function getChildIds(catItem) {
  const retArr = [];
  const stack = [catItem];

  while (stack.length) {
    const current = stack.pop();
    retArr.push(current.id);

    if (current.children) {
      stack.push(...current.children);
    }
  }

  return retArr;
}
